/**
 * Finding the evening that suits everybody.
 *
 * The poll version of this question asked "which night?" and counted votes.
 * This asks the real question - which of these times *can you make* - and
 * takes three answers per time, because "at a push" is the answer half a
 * group has about half the evenings offered.
 *
 * Nobody publishes a calendar. The only thing anybody sees is what people
 * said about these particular evenings, and the overlap is drawn rather than
 * argued about: the best row is marked, and the host turns it into an event
 * with everybody who said yes already down as going.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { GameMark } from './GameMark';
import { Button, Field, Modal, useToast } from './ui';
import { Select } from './Select';
import type { Crew } from './Crew';

type Mark = 'yes' | 'maybe' | 'no';

type Slot = {
  i: number;
  at: number;
  durationMin: number;
  yes: number;
  maybe: number;
  no: number;
  who: PublicUser[];
  mine: Mark | null;
};

export type WhenPoll = {
  id: string;
  title: string;
  gameId: string | null;
  createdBy: string;
  createdAt: number;
  closesAt: number;
  closed: boolean;
  answered: number;
  members: number;
  imIn: boolean;
  best: number;
  slots: Slot[];
};

const MARKS: { id: Mark; label: string; short: string; icon: string; tone: string }[] = [
  { id: 'yes', label: 'Works', short: 'Yes', icon: 'check', tone: 'var(--good)' },
  { id: 'maybe', label: 'At a push', short: 'Maybe', icon: 'hourglass', tone: 'var(--warn)' },
  { id: 'no', label: 'Cannot', short: 'No', icon: 'close', tone: 'var(--bad)' }
];

/** "Fri 19 Sep · 8pm" */
function when(at: number): string {
  const d = new Date(at);
  return d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })
    + ' · ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function WhenFree({ crew, canManage }: { crew: Crew; canManage: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [asking, setAsking] = useState(false);

  const q = useQuery({
    queryKey: ['crew-when', crew.id],
    queryFn: () => api.get<{ polls: WhenPoll[] }>('/api/crews/' + crew.id + '/availability'),
    enabled: !!crew.myRole,
    staleTime: 20_000
  });

  const mark = useMutation({
    mutationFn: ({ id, marks }: { id: string; marks: Record<number, Mark> }) =>
      api.post<{ poll: WhenPoll }>('/api/crews/' + crew.id + '/availability/' + id, { marks }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['crew-when', crew.id] }),
    onError: (err: Error) => toast(err.message, 'bad')
  });

  const settle = useMutation({
    mutationFn: ({ id, slot }: { id: string; slot: number }) =>
      api.post('/api/crews/' + crew.id + '/availability/' + id + '/event', { slot }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crew-when', crew.id] });
      void queryClient.invalidateQueries({ queryKey: ['crew'] });
      toast('It is in the calendar, and everybody who said yes is down as going', 'good');
    },
    onError: (err: Error) => toast(err.message, 'bad')
  });

  const drop = useMutation({
    mutationFn: (id: string) => api.del('/api/crews/' + crew.id + '/availability/' + id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['crew-when', crew.id] }),
    onError: (err: Error) => toast(err.message, 'bad')
  });

  if (!crew.myRole) return null;
  const polls = q.data?.polls ?? [];
  if (!polls.length && !canManage) return null;

  return (
    <section className="pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <div className="mb-3 flex items-center gap-2.5">
        <Icon name="calendar" size={18} className="text-ink" />
        <h3 className="m-0 flex-1 text-[16px] font-bold text-ink">When is everyone free?</h3>
        {canManage ? <Button size="sm" kind="outline" onClick={() => setAsking(true)}>Ask</Button> : null}
      </div>

      {!polls.length ? (
        <p className="m-0 text-[13.5px] text-mute">
          Offer a handful of evenings and everybody says which ones work. Pugo shows where they overlap - nobody has to share a calendar.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {polls.map((poll) => (
            <li key={poll.id} className="rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3">
              <div className="mb-2 flex items-center gap-2">
                {poll.gameId ? <GameMark id={poll.gameId} size={22} plain /> : null}
                <div className="min-w-0 flex-1">
                  <p className="m-0 truncate text-[14px] font-bold text-ink">{poll.title}</p>
                  <p className="m-0 text-[12px] text-mute">
                    {poll.answered} of {poll.members} answered
                    {poll.closed ? ' · closed' : ''}
                  </p>
                </div>
                {canManage ? (
                  <button
                    type="button"
                    aria-label={'Drop ' + poll.title}
                    onClick={() => drop.mutate(poll.id)}
                    className="grid h-7 w-7 flex-none place-items-center rounded-full text-ghost transition-colors hover:bg-surface-3 hover:text-ink"
                  >
                    <Icon name="close" size={13} />
                  </button>
                ) : null}
              </div>

              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {poll.slots.map((slot) => {
                  const best = slot.i === poll.best && slot.yes > 0;
                  return (
                    <li
                      key={slot.i}
                      className={cn(
                        'rounded-[calc(12px*var(--radius-mul,1))] p-2',
                        best ? 'bg-[color-mix(in_srgb,var(--good)_12%,var(--bg-2))] shadow-[inset_0_0_0_1.5px_color-mix(in_srgb,var(--good)_45%,transparent)]' : 'bg-surface-1'
                      )}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5 text-[13.5px] font-semibold text-ink">
                            {when(slot.at)}
                            {best ? <span className="rounded-full bg-good px-2 py-px text-[10.5px] font-bold text-[var(--bg-0)]">Best</span> : null}
                          </span>
                          <span className="block text-[11.5px] text-mute">
                            {slot.yes} can · {slot.maybe} at a push{slot.no ? ' · ' + slot.no + ' cannot' : ''}
                          </span>
                        </span>

                        {/* What everybody said, so a host can see the shape. */}
                        <span className="flex flex-none items-center">
                          {slot.who.slice(0, 5).map((u, i) => (
                            <span key={u.id} className="rounded-full ring-2 ring-[var(--bg-1)]" style={{ marginLeft: i ? -8 : 0 }}>
                              <Avatar user={u} size={20} />
                            </span>
                          ))}
                        </span>

                        {!poll.closed ? (
                          <span className="flex flex-none items-center gap-1">
                            {MARKS.map((m) => {
                              const on = slot.mine === m.id;
                              return (
                                <button
                                  key={m.id}
                                  type="button"
                                  aria-pressed={on}
                                  title={m.label}
                                  onClick={() => {
                                    const marks: Record<number, Mark> = {};
                                    for (const s2 of poll.slots) if (s2.mine) marks[s2.i] = s2.mine;
                                    marks[slot.i] = m.id;
                                    mark.mutate({ id: poll.id, marks });
                                  }}
                                  className={cn(
                                    'grid h-8 w-8 place-items-center rounded-[9px] outline-none transition-colors',
                                    on ? 'text-[var(--bg-0)]' : 'bg-surface-2 text-mute hover:bg-surface-3 hover:text-ink'
                                  )}
                                  style={on ? { background: m.tone } : undefined}
                                >
                                  <Icon name={m.icon} size={13} />
                                  <span className="sr-only">{m.label}</span>
                                </button>
                              );
                            })}
                          </span>
                        ) : null}

                        {canManage && !poll.closed && slot.yes > 0 ? (
                          <Button size="sm" kind={best ? 'primary' : 'plain'} onClick={() => settle.mutate({ id: poll.id, slot: slot.i })}>
                            Make it this one
                          </Button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>

              {!poll.imIn && !poll.closed ? (
                <p className="m-0 mt-2 text-[12px] text-mute">You have not said yet - mark the ones that work and the overlap updates for everybody.</p>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {asking ? <AskWhen crew={crew} onClose={() => setAsking(false)} /> : null}
    </section>
  );
}

/* ------------------------------------------------------------ asking */

/** The next fourteen evenings, at a sensible hour, as offerable slots. */
function evenings(hour: number): { at: number; label: string }[] {
  const out: { at: number; label: string }[] = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    d.setHours(hour, 0, 0, 0);
    if (d.getTime() < Date.now()) continue;
    out.push({ at: d.getTime(), label: d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }) });
  }
  return out;
}

function AskWhen({ crew, onClose }: { crew: Crew; onClose: () => void }) {
  const catalog = useSession((s) => s.catalog);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState('When is everyone free?');
  const [gameId, setGameId] = useState('');
  const [hour, setHour] = useState(20);
  const [picked, setPicked] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  const days = evenings(hour);

  async function save() {
    if (picked.length < 2) { toast('Offer at least two times.', 'bad'); return; }
    setBusy(true);
    try {
      await api.post('/api/crews/' + crew.id + '/availability', {
        title: title.trim(),
        gameId: gameId || null,
        days: 3,
        slots: picked.map((at) => ({ at, durationMin: 120 }))
      });
      void queryClient.invalidateQueries({ queryKey: ['crew-when', crew.id] });
      toast('Asked. Everybody in the crew was told once.', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Ask when everybody is free"
      subtitle="Offer the evenings that would work for you. Everyone marks which ones work for them."
      onClose={onClose}
      footer={
        <>
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void save()}>{busy ? 'Asking…' : 'Ask the crew'}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="What for">
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 60))}
            className="h-[var(--h-md)] w-full rounded-[calc(12px*var(--radius-mul,1))] border border-line bg-surface-1 px-3 text-[14px] outline-none transition-colors focus:border-accent"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Game">
            <Select
              value={gameId}
              onChange={setGameId}
              options={[{ value: '', label: 'Any game' }, ...(catalog?.games ?? []).map((g) => ({ value: g.id, label: g.name }))]}
            />
          </Field>
          <Field label="What time">
            <Select
              value={String(hour)}
              onChange={(v) => { setHour(Number(v)); setPicked([]); }}
              options={[18, 19, 20, 21, 22].map((h) => ({
                value: String(h),
                label: new Date(2020, 0, 1, h).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
              }))}
            />
          </Field>
        </div>

        <div>
          <span className="mb-2 block text-[13.5px] font-semibold text-ink">Which evenings to offer</span>
          <div className="flex flex-wrap gap-1.5">
            {days.map((d) => {
              const on = picked.includes(d.at);
              return (
                <button
                  key={d.at}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setPicked((list) => on ? list.filter((x) => x !== d.at) : [...list, d.at].sort((a, b) => a - b))}
                  className={cn(
                    'h-9 rounded-full px-3.5 text-[12.5px] font-semibold outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                    on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
                  )}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 mb-0 text-[12px] text-mute">
            {picked.length ? picked.length + ' offered' : 'Pick two or more.'} Everybody answers works, at a push, or cannot.
          </p>
        </div>
      </div>
    </Modal>
  );
}
